/**
 * ScribeRealtimeSTT — ElevenLabs Scribe v2 Realtime speech-to-text over
 * WebSocket (Task #530 rework).
 *
 * WHY THIS EXISTS (and why webkitSpeechRecognition is now only a Chromium
 * fallback): the browser's built-in SpeechRecognition opens its OWN
 * microphone capture session at the OS level, alongside our getUserMedia
 * stream. On macOS Safari that second concurrent capture — on top of a
 * live WebGL scene — is exactly the contention that froze the tab, and
 * WebKit has shipped fixes in this area as recently as mid-2026. This
 * engine transcribes from THE one shared capture stream instead: no second
 * capture, no Apple dictation dependency, identical behavior on every
 * browser, ~150 ms partials.
 *
 * Wire protocol (verified against the official elevenlabs-js SDK source):
 *   send → {"message_type":"input_audio_chunk","audio_base_64":"…",
 *           "commit":false,"sample_rate":16000}
 *   recv ← {"message_type":"session_started"|"partial_transcript"|
 *           "final_transcript"|"committed_transcript"|…, "text":"…"}
 *   err  ← {"message_type":"error"|…, "error":"<code>"}
 *
 * Auth: a single-use token minted by our server (/api/stt/scribe-token).
 * The API key never reaches the client; browsers can't set WS headers, so
 * the token rides a query parameter exactly as the docs prescribe.
 *
 * Audio path: capture stream → MediaStreamSource → AudioWorklet
 * (ScriptProcessor fallback) → Float32 → downsample to 16 kHz → Int16 →
 * base64 → one JSON frame every 250 ms. The AudioContext is created once
 * and suspended (not closed) between sessions — iOS has a small
 * AudioContext budget and create/close churn fed the jetsam kills.
 */

const SCRIBE_WS_BASE = "wss://api.elevenlabs.io/v1/speech-to-text/realtime";
const TOKEN_URL = "/api/stt/scribe-token";
const WORKLET_URL = "/static/worklets/pcm-capture.js?v=1";
const TARGET_RATE = 16000;
const SEND_INTERVAL_MS = 250;
const OPEN_TIMEOUT_MS = 9000;

/** Linear-interpolation downsample (mono). Good enough for speech→ASR. */
function downsampleTo(f32, fromRate, toRate) {
    if (fromRate === toRate) return f32;
    const ratio = fromRate / toRate;
    const n = Math.floor(f32.length / ratio);
    const out = new Float32Array(n);
    for (let i = 0; i < n; i++) {
        const pos = i * ratio;
        const i0 = Math.floor(pos);
        const i1 = Math.min(i0 + 1, f32.length - 1);
        const fr = pos - i0;
        out[i] = f32[i0] * (1 - fr) + f32[i1] * fr;
    }
    return out;
}

export class ScribeRealtimeSTT {
    /**
     * @param {Object} opts
     * @param {Function} [opts.onReady]      session_started received
     * @param {Function} [opts.onPartial]    (text) interim / stable-uncommitted
     * @param {Function} [opts.onCommitted]  (text) settled segment
     * @param {Function} [opts.onError]      (code, message) server error event
     * @param {Function} [opts.onClose]      (lastErrorCode|null) UNEXPECTED close
     * @param {Function} [opts.ledger]       (ev, detail) MIC-LEDGER hook
     */
    constructor(opts = {}) {
        this.onReady = opts.onReady || null;
        this.onPartial = opts.onPartial || null;
        this.onCommitted = opts.onCommitted || null;
        this.onError = opts.onError || null;
        this.onClose = opts.onClose || null;
        this._ledgerFn = opts.ledger || null;

        this.ws = null;
        this.active = false;
        this._gen = 0;

        this._ctx = null; // persistent (suspended between sessions)
        this._workletLoadedFor = null;
        this._srcNode = null;
        this._pumpNode = null;
        this._zeroGain = null;
        this._pumpKind = null;

        this._chunks = [];
        this._chunkLen = 0;
        this._sendTimer = null;

        this._lastError = null;
        this._sawPartial = false;
    }

    _ledger(ev, detail) {
        try {
            this._ledgerFn?.(ev, detail || {});
        } catch (_) {}
    }

    /**
     * Open a session on the given (already-acquired) capture stream.
     * Resolves once session_started arrives. Throws classified errors
     * (err.name: SttUnconfigured | SttBusy | SttTokenError | SttServiceError).
     */
    async start(stream) {
        if (this.active || this.ws) this.stop("restart");
        const gen = ++this._gen;
        const t0 = Date.now();

        const token = await this._fetchToken();
        if (gen !== this._gen) return; // superseded while minting

        await this._connect(token, gen, stream);
        if (gen !== this._gen) return;

        this.active = true;
        this._ledger("scribe-open", {
            ms: Date.now() - t0,
            pump: this._pumpKind || "?",
        });
    }

    /** Intentional teardown — never fires onClose. Safe to call twice. */
    stop(reason = "stop") {
        this._gen++;
        const hadWs = !!this.ws;
        this.active = false;

        if (this._sendTimer) {
            clearInterval(this._sendTimer);
            this._sendTimer = null;
        }
        // Last buffered audio out before the socket closes (best-effort).
        try {
            this._flushAudio();
        } catch (_) {}
        this._teardownPump();

        const ws = this.ws;
        this.ws = null;
        this._lastError = null;
        this._sawPartial = false;
        if (ws) {
            try {
                ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
                ws.close(1000);
            } catch (_) {}
        }
        // Suspend (never close) the context — reused next session.
        try {
            this._ctx?.suspend?.();
        } catch (_) {}
        if (hadWs) this._ledger("scribe-stop", { reason });
    }

    async _fetchToken() {
        let res;
        try {
            res = await fetch(TOKEN_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: "{}",
            });
        } catch (_) {
            const e = new Error("Token request failed (network)");
            e.name = "SttTokenError";
            throw e;
        }
        if (res.status === 503) {
            const e = new Error("Realtime STT is not configured");
            e.name = "SttUnconfigured";
            throw e;
        }
        if (res.status === 429) {
            const e = new Error("Token minting rate-limited");
            e.name = "SttBusy";
            throw e;
        }
        if (!res.ok) {
            const e = new Error("Token mint failed: " + res.status);
            e.name = "SttTokenError";
            throw e;
        }
        const data = await res.json().catch(() => null);
        if (!data || !data.token) {
            const e = new Error("Token missing in mint response");
            e.name = "SttTokenError";
            throw e;
        }
        this._ledger("scribe-token", { ok: true });
        return data.token;
    }

    _connect(token, gen, stream) {
        return new Promise((resolve, reject) => {
            const qs = new URLSearchParams({
                model_id: "scribe_v2_realtime",
                token,
                audio_format: "pcm_16000",
                commit_strategy: "vad",
                // End-of-thought silence before a segment commits. Tuned for
                // conversation turn-taking (Call) — Dictate just accumulates
                // segments, so the same value reads fine there.
                vad_silence_threshold_secs: "1.2",
            });
            const ws = new WebSocket(`${SCRIBE_WS_BASE}?${qs.toString()}`);
            this.ws = ws;
            let settled = false;

            const openTimer = setTimeout(() => {
                if (settled) return;
                settled = true;
                const e = new Error("Scribe session timed out");
                e.name = "SttServiceError";
                try {
                    ws.close();
                } catch (_) {}
                reject(e);
            }, OPEN_TIMEOUT_MS);

            ws.onopen = () => {
                this._ledger("scribe-ws-open", {});
                // Start feeding audio immediately — session_started follows
                // within a beat and the server tolerates early frames.
                this._startPump(stream, gen).catch((err) => {
                    if (settled) return;
                    settled = true;
                    clearTimeout(openTimer);
                    try {
                        ws.close();
                    } catch (_) {}
                    reject(err);
                });
            };

            ws.onmessage = (evt) => {
                let msg;
                try {
                    msg = JSON.parse(evt.data);
                } catch (_) {
                    return;
                }
                const mt = msg.message_type || "";
                if (mt === "session_started") {
                    if (!settled) {
                        settled = true;
                        clearTimeout(openTimer);
                        this._ledger("scribe-session-started", {});
                        try {
                            this.onReady?.();
                        } catch (_) {}
                        resolve();
                    }
                    return;
                }
                if (mt === "partial_transcript" || mt === "final_transcript") {
                    if (!this._sawPartial) {
                        this._sawPartial = true;
                        this._ledger("scribe-first-partial", {});
                    }
                    try {
                        this.onPartial?.(msg.text || "");
                    } catch (_) {}
                    return;
                }
                if (mt === "committed_transcript") {
                    try {
                        this.onCommitted?.(msg.text || "");
                    } catch (_) {}
                    return;
                }
                if (
                    mt === "committed_transcript_with_timestamps" ||
                    mt === "final_transcript_with_timestamps" ||
                    mt === "committed_transcript_entities" ||
                    mt === "warning"
                ) {
                    return; // informational — not requested / not used
                }
                // Anything else is an error event (message_type "error" or a
                // dedicated *_error type; the code rides `error`).
                const code = String(msg.error || mt || "error");
                this._lastError = code;
                this._ledger("scribe-error", { code });
                try {
                    this.onError?.(code, msg.message || "");
                } catch (_) {}
            };

            ws.onerror = () => {
                if (settled) return;
                settled = true;
                clearTimeout(openTimer);
                const e = new Error("Scribe socket failed to open");
                e.name = "SttServiceError";
                reject(e);
            };

            ws.onclose = (evt) => {
                clearTimeout(openTimer);
                if (!settled) {
                    settled = true;
                    const e = new Error("Scribe socket closed during setup");
                    e.name = "SttServiceError";
                    reject(e);
                    return;
                }
                if (gen !== this._gen) return; // intentional stop/supersede
                const lastErr = this._lastError || null;
                this.active = false;
                this._ledger("scribe-close", {
                    code: evt && evt.code,
                    err: lastErr || "-",
                });
                this._teardownPump();
                this.ws = null;
                try {
                    this.onClose?.(lastErr);
                } catch (_) {}
            };
        });
    }

    async _startPump(stream, gen) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) {
            const e = new Error("Web Audio unavailable");
            e.name = "SttServiceError";
            throw e;
        }
        if (!this._ctx || this._ctx.state === "closed") {
            // Ask for 16 kHz so most frames need no resampling; Safari may
            // ignore or reject the hint — any rate works, we downsample.
            try {
                this._ctx = new AC({ sampleRate: TARGET_RATE });
            } catch (_) {
                this._ctx = new AC();
            }
            this._workletLoadedFor = null;
        }
        try {
            await this._ctx.resume();
        } catch (_) {}
        const ctx = this._ctx;

        this._srcNode = ctx.createMediaStreamSource(stream);
        this._zeroGain = ctx.createGain();
        this._zeroGain.gain.value = 0; // pull the graph without audible output

        let pump = null;
        if (ctx.audioWorklet && ctx.audioWorklet.addModule) {
            try {
                if (this._workletLoadedFor !== ctx) {
                    await ctx.audioWorklet.addModule(WORKLET_URL);
                    this._workletLoadedFor = ctx;
                }
                const node = new AudioWorkletNode(ctx, "pcm-capture", {
                    numberOfInputs: 1,
                    numberOfOutputs: 1,
                    outputChannelCount: [1],
                });
                node.port.onmessage = (e) => {
                    if (gen === this._gen) this._pushSamples(e.data);
                };
                this._srcNode.connect(node);
                node.connect(this._zeroGain);
                this._zeroGain.connect(ctx.destination);
                this._pumpNode = node;
                pump = "worklet";
            } catch (err) {
                this._ledger("scribe-worklet-fallback", {
                    msg: String((err && err.message) || err).slice(0, 80),
                });
            }
        }
        if (!pump) {
            // Deprecated but universally solid — incl. older Safari.
            const sp = ctx.createScriptProcessor(4096, 1, 1);
            sp.onaudioprocess = (e) => {
                if (gen === this._gen) {
                    this._pushSamples(
                        new Float32Array(e.inputBuffer.getChannelData(0)),
                    );
                }
            };
            this._srcNode.connect(sp);
            sp.connect(this._zeroGain);
            this._zeroGain.connect(ctx.destination);
            this._pumpNode = sp;
            pump = "scriptprocessor";
        }
        this._pumpKind = pump;

        this._chunks = [];
        this._chunkLen = 0;
        if (this._sendTimer) clearInterval(this._sendTimer);
        this._sendTimer = setInterval(
            () => this._flushAudio(),
            SEND_INTERVAL_MS,
        );
    }

    _pushSamples(f32) {
        if (!f32 || !f32.length) return;
        this._chunks.push(f32);
        this._chunkLen += f32.length;
        // Hard cap ~5s at context rate — a stuck sender must not eat memory.
        const cap = (this._ctx ? this._ctx.sampleRate : 48000) * 5;
        while (this._chunkLen > cap && this._chunks.length > 1) {
            this._chunkLen -= this._chunks.shift().length;
        }
    }

    _flushAudio(forceCommit = false) {
        const ws = this.ws;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        if (!this._chunkLen) return;

        const all = new Float32Array(this._chunkLen);
        let o = 0;
        for (const c of this._chunks) {
            all.set(c, o);
            o += c.length;
        }
        this._chunks = [];
        this._chunkLen = 0;

        const inRate = this._ctx ? this._ctx.sampleRate : TARGET_RATE;
        const mono = downsampleTo(all, inRate, TARGET_RATE);
        const pcm = new Int16Array(mono.length);
        for (let i = 0; i < mono.length; i++) {
            const s = Math.max(-1, Math.min(1, mono[i]));
            pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        const bytes = new Uint8Array(pcm.buffer);
        let bin = "";
        for (let i = 0; i < bytes.length; i += 0x8000) {
            bin += String.fromCharCode.apply(
                null,
                bytes.subarray(i, i + 0x8000),
            );
        }
        try {
            ws.send(
                JSON.stringify({
                    message_type: "input_audio_chunk",
                    audio_base_64: btoa(bin),
                    commit: !!forceCommit,
                    sample_rate: TARGET_RATE,
                }),
            );
        } catch (_) {}
    }

    _teardownPump() {
        try {
            if (this._pumpNode) this._pumpNode.onaudioprocess = null;
        } catch (_) {}
        try {
            if (this._pumpNode && this._pumpNode.port) {
                this._pumpNode.port.onmessage = null;
            }
        } catch (_) {}
        try {
            this._pumpNode?.disconnect();
        } catch (_) {}
        try {
            this._srcNode?.disconnect();
        } catch (_) {}
        try {
            this._zeroGain?.disconnect();
        } catch (_) {}
        this._pumpNode = this._srcNode = this._zeroGain = null;
        this._pumpKind = null;
        this._chunks = [];
        this._chunkLen = 0;
        if (this._sendTimer) {
            clearInterval(this._sendTimer);
            this._sendTimer = null;
        }
    }
}
